/*
 * File: fascicle-list-alone.component.ts                                      *
 * Project: catalog-cloud                                                      *
 * File Created: Friday, 3rd June 2022 11:54:57                                *
 * Author: Tomás Muniesa (tomas@cege.es)                                       *
 * -----                                                                       *
 * Last Modified: Thursday, 2nd March 2023 14:40:19                            *
 * Modified By: Tomás Muniesa (tomas@cege.es>)                                 *
 * -----                                                                       *
 * Copyright 2021 - 2022 Nousmedis, CeGe                                       *
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Fascicle } from '@nousmedis/cc-types';
import { CatalogService } from '../../../services/catalog.service';
import { FascicleService } from '../../../services/fascicle.service';
import { FascicleSocketService } from '../../../services/fascicle-socket.service';
import { ProductTagsService } from '../../../services/product-tags.service';
import { ProductCategoriesService } from '../../../services/product-categories.service';
import { UserService } from '../../../../user/services/user.service';
import { UserModel } from '../../../../user/models/user.model';
import { ApiService } from '../../../../api/services/api.service';
import { CookieService } from 'ngx-cookie-service';
import { User } from '@nousmedis/guard-types/dist/frontend';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { TranslateService } from '@ngx-translate/core';
// import { PopInfoService } from '../../../pop-info/services/pop-info-service.service';

@Component({
  selector: 'app-fascicle-list-alone',
  templateUrl: './fascicle-list-alone.component.html',
  styleUrls: ['./fascicle-list-alone.component.scss'],
})
export class FascicleListAloneComponent implements OnInit {
  dataSuscribe: any;
  loadingSuscribe: any;
  socketSuscribe: Subscription;

  pendingUnblockFascicle: Fascicle | null = null;
  unblockDialogVisible = false;
  private unblockResultSub: Subscription;

  idCatalog: string;
  catalogName: string = '';

  dataLoaded: boolean = false;
  loading: boolean = false;

  dataSource: Fascicle[];

  row: any;
  pageFrom = 0;
  user: User;

  private userModel: UserModel;
  private userNameCache = new Map<string, string>();

  tagMap: Record<string, string> = {};
  categoryMap: Record<string, string> = {};

  //Table columns displayed (and order)
  displayedColumns: string[] = [
    'moveRow',
    'name',
    'author',
    'updatedAt',
    'pages',
    'actions',
  ];

  constructor(
    private fascicleService: FascicleService,
    private catalogService: CatalogService,
    private messageService: MessageService,
    private userService: UserService,
    private socket: FascicleSocketService,
    private route: ActivatedRoute,
    private router: Router,
    private trans: TranslateService,
    private confirmService: ConfirmationService,
    private tagsService: ProductTagsService,
    private categoriesService: ProductCategoriesService,
    api: ApiService,
    cookie: CookieService,
  ) {
    this.dataSource = [];
    this.userModel = new UserModel(api, cookie);
  }

  ngOnInit(): void {
    this.user = this.userService.getCurrentUser();

    this.socketSuscribe = this.socket.fasciclesChanged.subscribe((change) => {
      this.reload();
    });

    this.dataSuscribe = this.fascicleService.dataTableFascicles.subscribe(
      (data) => {
        this.dataSource = data;
      }
    );

    this.loadingSuscribe = this.fascicleService.loadingFascicle.subscribe(
      (load) => {
        this.loading = load;
      }
    );

    this.route.params.subscribe((params) => {
      this.idCatalog = params['idCatalog'];
      this.catalogService.getCatalog(this.idCatalog).subscribe((data) => {
        if (data) this.catalogName = data.name;
      });
      this.loadFascicles();
    });

    this.unblockResultSub = this.socket.unblockResult.subscribe((result) => {
      if (this.pendingUnblockFascicle && result.fascicleId === this.pendingUnblockFascicle.id) {
        this.unblockDialogVisible = false;
        this.pendingUnblockFascicle = null;
        if (result.accepted) {
          this.router.navigate(['/catalog', this.idCatalog, 'fascicle', result.fascicleId]);
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: this.trans.instant('FASCICLE.UNBLOCK_REJECTED'),
            detail: this.trans.instant('FASCICLE.UNBLOCK_REJECTED_DETAIL'),
            key: 'root-msg',
          });
        }
      }
    });
  }

  loadFascicles() {
    if (!this.idCatalog) return;
    this.loadTagsAndCategories();
    this.fascicleService.fetch(this.idCatalog).subscribe((data) => {
      if (!data) return;
      const filtered = (data as any[]).filter(f => f != null);
      this.dataSource = this.fascicleService.setFromToPages(filtered)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      this.dataSource.forEach(f => (f as any).menuItems = this.getMenuItems(f));
      this.dataLoaded = true;
      this.resolveBlockedByNames();
    });
  }

  private loadTagsAndCategories(): void {
    this.tagsService.getByCatalog(this.idCatalog).subscribe((tags: any[]) => {
      if (!Array.isArray(tags)) return;
      tags.forEach(t => { if (t?.id && t?.name) this.tagMap[t.id] = t.name; });
    });
    this.categoriesService.getByCatalog(this.idCatalog).subscribe((cats: any[]) => {
      if (!Array.isArray(cats)) return;
      cats.forEach(c => { if (c?.id && c?.name) this.categoryMap[c.id] = c.name; });
    });
  }

  resolvedChips(ids: string[], map: Record<string, string>, max = 4): string[] {
    if (!ids?.length) return [];
    return ids.map(id => map[id]).filter(Boolean).slice(0, max);
  }

  private async resolveBlockedByNames(): Promise<void> {
    const ids = [
      ...new Set(
        this.dataSource
          .filter(f => f.blocked && typeof f.blockedBy === 'string' && f.blockedBy && !this.userNameCache.has(f.blockedBy as string))
          .map(f => f.blockedBy as string)
      )
    ];
    await Promise.all(ids.map(async id => {
      try {
        const u = await this.userModel.getUserById(id);
        const surname = u.surname ? u.surname.charAt(0).toUpperCase() + '.' : '';
        this.userNameCache.set(id, `${u.name} ${surname}`.trim());
      } catch {
        this.userNameCache.set(id, id);
      }
    }));
  }

  getBlockedByDisplay(fascicle: Fascicle): string {
    const raw = (fascicle as any).blockedBy;
    if (!raw) return '';
    if (typeof raw === 'object' && raw.name) {
      const surname = raw.surname ? raw.surname.charAt(0).toUpperCase() + '.' : '';
      return `${raw.name} ${surname}`.trim();
    }
    return this.userNameCache.get(raw) ?? raw;
  }

  isSameUserBlock(fascicle: Fascicle): boolean {
    const raw = (fascicle as any).blockedBy;
    if (!raw) return false;
    const id = typeof raw === 'object' ? (raw.id ?? raw._id) : raw;
    return id === this.user?.id;
  }

  takeOverFascicle(fascicle: Fascicle): void {
    this.socket.sendTakeOver(this.idCatalog, fascicle.id, this.user.id);
    this.router.navigate(['/catalog', this.idCatalog, 'fascicle', fascicle.id]);
  }

  ngOnDestroy(): void {
    if (this.unblockResultSub) this.unblockResultSub.unsubscribe();
    this.dataSuscribe.unsubscribe();
    this.loadingSuscribe.unsubscribe();
    this.socketSuscribe.unsubscribe();
    // this.socket.fasciclesChanged.unsubscribe();
  }

  handleMouseOver(row: any) {
    if (!this.loading) {
      row.isHover = true;
      if (document.getElementById('actions-row-' + row.id))
        document
          .getElementById('actions-row-' + row.id)
          .classList.remove('actions-not-visible');
      if (document.getElementById('move-' + row.id))
        document
          .getElementById('move-' + row.id)
          .classList.remove('move-fascicle-not-visible');
    }
  }

  handleMouseLeave(row: any) {
    if (!this.loading) {
      row.isHover = false;
      if (document.getElementById('actions-row-' + row.id))
        document
          .getElementById('actions-row-' + row.id)
          .classList.add('actions-not-visible');
      if (document.getElementById('move-' + row.id))
        document
          .getElementById('move-' + row.id)
          .classList.add('move-fascicle-not-visible');
    }
  }

  changeFascicleOrder(event: any) {
    this.loading = true;
    const prevIndex = this.dataSource.findIndex((d) => d === event.item.data);
    console.log(
      'antes del movearray:' + this.dataSource.map((m) => m.name).join(',')
    );

    console.log(
      'despues del movearray:' + this.dataSource.map((m) => m.name).join(',')
    );

    // this.dataSource.data = this.fascicleService.setFromToPages(
    //   this.dataSource.data
    // );

    //this.table.renderRows();
    // this.fascicleService.updateFascicleOrder(
    //   this.idCatalog,
    //   this.dataSource
    // );
    // this.fascicleService.setFromToPages(this.dataSource);
    this.loading = false;
  }

  openFascicleSettings(id: string) {
    this.router.navigate(['/catalog', this.idCatalog, 'fascicles', id, 'edit']);
  }

  editFascicle(id: string) {
    this.fascicleService.openFascicleForm(id, this.idCatalog);
  }

  reload() {
    if (!this.idCatalog) return;
    this.catalogService.getCatalog(this.idCatalog).subscribe((data) => {
      if (!data?.fascicles) return;
      this.dataSource = this.fascicleService.setFromToPages(data.fascicles)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      this.dataSource.forEach(f => (f as any).menuItems = this.getMenuItems(f));
      this.resolveBlockedByNames();
    });
  }

  clone(id: string) {
    this.confirmService.confirm({
      header: this.trans.instant('Clonar fascículo'),
      message: this.trans.instant('Seguro que quieres clonar el fascículo?'),
      icon: 'pi pi-clone',
      acceptIcon: 'fa fa-check',
      acceptLabel: this.trans.instant('FORM.YES'),
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-danger',
      rejectIcon: 'fa fa-times',
      key: 'cloneFascicleConfirm',
      accept: () => {
        this.fascicleService.clone(this.idCatalog, id).subscribe((data) => {
          this.dataLoaded = true;
          this.reload();
          this.messageService.add({
            severity: 'success',
            summary: 'Fascículo clonado',
            detail: 'Clonación de fasciculo',
            key: 'root-msg',
          });
        });
      },
    });
  }

  delete(id: string, catalog: string) {
    this.confirmService.confirm({
      header: this.trans.instant('Eliminar fascículo'),
      message: this.trans.instant('Seguro que quieres eliminar el fascículo?'),
      icon: 'fa fa-trash',
      acceptIcon: 'fa fa-check',
      acceptLabel: this.trans.instant('FORM.YES'),
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-danger',
      rejectIcon: 'fa fa-times',
      key: 'deleteFascicleConfirm',
      accept: () => {
        this.fascicleService.delete(catalog, id).subscribe((deleted) => {
          this.reload();
          this.messageService.add({
            severity: 'info',
            summary: 'Fascículo eliminado',
            detail: 'Eliminación de fasciculo',
            key: 'root-msg',
          });
        });
      }
    });
  }

  showFromTo(fascicle: Fascicle) {
    let from = this.pageFrom + 1;
    let to = fascicle.totalPages + this.pageFrom;

    this.pageFrom = to;
    return from + ' - ' + to;
  }

  requestUnBlock(fascicle: Fascicle) {
    this.pendingUnblockFascicle = fascicle;
    this.unblockDialogVisible = true;
    this.fascicleService.get(this.idCatalog, fascicle.id).subscribe((fasc) => {
      this.socket.sendUnblockRequestFascicle(
        this.idCatalog,
        fascicle.id,
        this.user.id,
        fasc.blockedBy,
        `${this.user.name} ${this.user.surname ?? ''}`.trim()
      );
    });
  }

  goToFascicle(fascicleId: string) {
    this.router.navigate(['/catalog', this.idCatalog, 'fascicle', fascicleId]);
  }

  getMenuItems(fascicle: Fascicle): MenuItem[] {
    return [
      {
        icon: 'pi pi-cog',
        label: this.trans.instant('FASCICLE.EDIT_PROPERTIES'),
        command: () => this.openFascicleSettings(fascicle.id),
      },
      {
        icon: 'pi pi-clone',
        label: this.trans.instant('FASCICLE.CLONE'),
        command: () => this.clone(fascicle.id),
      },
      { separator: true },
      {
        icon: 'pi pi-trash',
        label: this.trans.instant('FASCICLE.DELETE'),
        command: () => this.delete(fascicle.id, this.idCatalog),
      },
    ];
  }

  newFascicle() {
    this.fascicleService.newModalFascicle(this.idCatalog);
  }

  generateFascicle() {
    this.router.navigate(['/catalog', this.idCatalog, 'fascicles', 'new-auto']);
  }

  reOrderFascicle(event: any) {
    // Actualizar posiciones en el array local (p-table ya lo reordenó visualmente)
    this.dataSource.forEach((f, i) => {
      f.position = i + 1;
    });
    // Recalcular fromto visualmente
    this.dataSource = this.fascicleService.setFromToPages([...this.dataSource]);
    // Persistir en background sin recargar
    this.fascicleService.updateFascicleOrder(this.idCatalog, this.dataSource);
  }

  // exportToSalesLayer(){
  //   this.confirmService.confirm({
  //     message: this.trans.instant("CATALOG.EXPORT_QUESTION"),
  //     header: this.trans.instant("CATALOG.EXPORT_TO_SL"),
  //     closeOnEscape: true,
  //     icon: 'fa fa-exclamation-triangle',
  //     acceptLabel: this.trans.instant("FORM.YES"),
  //     key: 'deleteFascicleConfirm',
  //     accept: () => {
  //       this.catalogService.openUploadForm(this.idCatalog)
  //     }
  //   })
  // }

  exportToSyncWall() {
    this.fascicleService.openUploadFormToSyncwall(this.idCatalog);
  }

  exportToCSV() {
    this.confirmService.confirm({
      message: this.trans.instant('CATALOG.EXPORT_QUESTION'),
      header: this.trans.instant('MENU.EXPORT_CSV'),
      closeOnEscape: true,
      icon: 'fa fa-exclamation-triangle',
      acceptLabel: this.trans.instant('FORM.YES'),
      key: 'exportCsvConfirm',
      accept: () => {
        this.catalogService.exportCsv(this.idCatalog);
      },
    });
  }
}
